import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, ButtonBase, Collapse, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { PATIENT_FORM_TOKENS as T, PatientChip } from './PatientFormPrimitives'

/**
 * One tappable row that opens a group of optional fields (Address, Attendants, Other details).
 * `alwaysOpen` renders the content with a plain title (view / edit-existing flows).
 * Children stay mounted while collapsed so typing never loses focus across a toggle.
 */
export default function PatientCollapsibleSection({
  id,
  title,
  summary,
  count = 0,
  countLabel,
  defaultExpanded = false,
  alwaysOpen = false,
  icon = null,
  children,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  useEffect(() => {
    if (defaultExpanded) setExpanded(true)
  }, [defaultExpanded])
  const isOpen = alwaysOpen || expanded

  if (alwaysOpen) {
    return (
      <Box sx={{ borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          {icon}
          <Typography component="div" sx={{ fontSize: '13.5px', fontWeight: 700, color: '#334155' }}>
            {title}
          </Typography>
          {count > 0 && countLabel && <PatientChip>{countLabel}</PatientChip>}
        </Box>
        {children}
      </Box>
    )
  }

  return (
    <Box>
      <ButtonBase
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls={id}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          padding: { xs: '12px 12px', sm: '10px 12px' },
          minHeight: { xs: '48px', sm: '44px' },
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          background: isOpen ? T.surface : T.surfaceSoft,
          transition: 'background 0.15s ease',
          textAlign: 'left',
          '&:hover': { background: '#f1f5f9' },
          '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${T.accentRing}` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flexWrap: 'wrap' }}>
          {icon}
          <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 700, color: '#334155' }}>
            {title}
          </Typography>
          {count > 0 && countLabel && <PatientChip>{countLabel}</PatientChip>}
          {!isOpen && summary && (
            <Typography
              component="span"
              sx={{
                fontSize: '12.5px',
                color: T.muted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: '60vw', sm: '360px' },
              }}
            >
              {summary}
            </Typography>
          )}
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: '20px',
            color: T.muted,
            flexShrink: 0,
            transform: isOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease',
          }}
        />
      </ButtonBase>
      <Collapse in={isOpen} timeout={180} unmountOnExit={false}>
        <Box id={id} sx={{ paddingTop: '12px' }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  )
}
